import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, Map } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export const ProtectedRoute = ({ children, requireOwner = false, requireSuperOwner = false }) => {
  const location = useLocation();
  const { user, loading, isAuthenticated, isOwner, isSuperOwner, checkAuth } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  // Safety: if loading takes > 5 seconds, force a re-check
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      if (loading) {
        setTimedOut(true);
        // Force a re-check using localStorage Bearer token
        checkAuth(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [loading, checkAuth]);

  // Still checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-forest-900 flex items-center justify-center">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-terracotta rounded-sm flex items-center justify-center">
              <Map className="w-7 h-7 text-white" />
            </div>
          </div>
          <Loader2 className="w-8 h-8 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/60 text-sm">
            {timedOut ? 'Still loading, please wait...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Route requires super owner
  if (requireSuperOwner && !isSuperOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  // Route requires owner but user is investor
  if (requireOwner && !isOwner) {
    return <Navigate to="/investor" replace />;
  }

  // Authenticated - render children with user context
  return React.cloneElement(children, { user });
};

// Higher order component for owner-only content within a page
export const OwnerOnly = ({ children, fallback = null }) => {
  const { isOwner } = useAuth();
  return isOwner ? children : fallback;
};

// Higher order component for super-owner-only content within a page
export const SuperOwnerOnly = ({ children, fallback = null }) => {
  const { isSuperOwner } = useAuth();
  return isSuperOwner ? children : fallback;
};

// Higher order component for investor-only content within a page
export const InvestorOnly = ({ children, fallback = null }) => {
  const { isInvestor } = useAuth();
  return isInvestor ? children : fallback;
};
